import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'

import RunnerGoalBubble from '@/components/Runner/RunnerGoalBubble'
import RunnerGoalEquation from '@/components/Runner/RunnerGoalEquation'
import RunnerGoalInvertLetters from '@/components/Runner/RunnerGoalInvertLetters'
import RunnerGoalList from '@/components/Runner/RunnerGoalList'
import RunnerGoalSyllable from '@/components/Runner/RunnerGoalSyllable'
import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'
import { SequenceCompletion } from '@/webgl/runner/scenarios/SequenceCompletion'
import { SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

import RunnerGoalDictation from './RunnerGoalDictation'
// import RunnerGoalShopping from "@/components/Runner/RunnerGoalShopping";

export default function RunnerGoal() {
  const { gameState } = useRunnerState()
  const nodeRef = useRef<HTMLDivElement>(null)

  const { gsap } = useGSAP()

  const completion = gameState.completion

  // const key = completion.?.length > 0 ? `${variant}::${goal?.join('_')}__${template}` : 'empty';
  const key =
    !!completion && !!completion.view && completion?.blocks?.length > 0 && completion?.blocks[0].goal?.length > 0
      ? `${completion?.view?.variant}::${completion.blocks.map((block) => block.goal.join('-')).join('_')}|${completion.view.template}}`
      : 'empty'

  const [cachedKey, setCachedKey] = useState<string | null>('empty')
  const [cachedCompletion, setCachedCompletion] = useState<SequenceCompletion>(null)
  const [cachedTemplate, setCachedTemplate] = useState<string>(null)
  const [cachedSuffix, setCachedSuffix] = useState<string>(null)
  const [exiting, setExiting] = useState(false)

  useLayoutEffect(() => {
    const updateCache = () => {
      setCachedKey(key)
      setCachedCompletion(completion)
      setCachedTemplate(completion?.view?.template)
      setCachedSuffix(completion?.view?.suffix)
    }

    let timeout = null
    if (cachedKey === 'empty') {
      updateCache()
    } else {
      setExiting(true)
      timeout = setTimeout(updateCache, 1000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, completion, gsap, setCachedKey])

  const onEnter = useCallback(() => {
    setExiting(false)
    if (cachedKey === 'empty') return

    gsap
      .timeline()
      .fromTo(nodeRef.current, { yPercent: -100 }, { yPercent: 0, duration: 0.7, ease: 'back.out(1.2)' }, 0)
  }, [cachedKey, nodeRef, gsap, setExiting])

  const onExit = useCallback(() => {
    if (cachedKey === 'empty') return
    gsap
      .timeline()
      .addLabel('start', 0)
      .fromTo(nodeRef.current, { yPercent: 0 }, { yPercent: -100, duration: 0.7, ease: 'power2.out' }, 'start')
      .fromTo(nodeRef.current, { opacity: 1 }, { opacity: 0, duration: 0.7, ease: 'power2.out' }, 'start')
  }, [cachedKey, nodeRef, gsap])

  return (
    <SwitchTransition mode='out-in'>
      <Transition
        key={cachedKey}
        nodeRef={nodeRef}
        appear
        onEnter={onEnter}
        onExit={onExit}
        timeout={{
          enter: cachedKey === 'empty' ? 0 : 700,
          exit: cachedKey === 'empty' ? 0 : 700,
        }}
      >
        <div className='RunnerGoal absolute top-0' ref={nodeRef}>
          {cachedCompletion?.view?.variant === SequenceViewVariant.Default && (
            <RunnerGoalList completion={cachedCompletion} />
          )}
          {cachedCompletion?.view?.variant === SequenceViewVariant.Equation && (
            <RunnerGoalEquation
              template={cachedTemplate}
              suffix={cachedSuffix}
              completion={cachedCompletion}
              exiting={exiting}
            />
          )}
          {cachedCompletion?.view?.variant === SequenceViewVariant.Bubble && (
            <RunnerGoalBubble goal={cachedCompletion.blocks?.[0].goal?.[0]} exiting={exiting} />
          )}
          {cachedCompletion?.view?.variant === SequenceViewVariant.InvertLetters && (
            <RunnerGoalInvertLetters completion={cachedCompletion} />
          )}
          {cachedCompletion?.view?.variant === SequenceViewVariant.Syllable && (
            <RunnerGoalSyllable completion={cachedCompletion} />
          )}
          {cachedCompletion?.view?.variant === SequenceViewVariant.Dictation && (
            <RunnerGoalDictation completion={cachedCompletion} />
          )}
          {/* {cachedCompletion?.view?.variant === SequenceViewVariant.Shopping && <RunnerGoalShopping completion={cachedCompletion} />} */}
        </div>
      </Transition>
    </SwitchTransition>
  )
}
